import { getList } from '@/requests/api/get-list';
import {
  ApartmentVacancyNotationConfig,
  IndustrialPremisesVacancyNotationConfig,
  OutdoorSectionVacancyNotationConfig,
  ParkingSpotVacancyNotationConfig,
  type Apartment,
  type ApartmentVacancyNotation,
  type IndustrialPremises,
  type IndustrialPremisesVacancyNotation,
  type OutdoorSection,
  type OutdoorSectionVacancyNotation,
  type ParkingSpot,
  type ParkingSpotVacancyNotation,
  type VacancyInvoice,
} from '@pigello/pigello-matrix';
import { chunkIds } from '../utils';
import type { TCustomList } from './types';
export async function handleCustomVacancyInvoiceNestedCase(
  listResponse: TCustomList<VacancyInvoice>
): Promise<TCustomList<VacancyInvoice>> {
  const apartmentVacancyNotationIds: Set<string> = new Set();
  const industrialPremisesVacancyNotationIds: Set<string> = new Set();
  const parkingSpotVacancyNotationIds: Set<string> = new Set();
  const outdoorSectionVacancyNotationIds: Set<string> = new Set();

  // Extract unique IDs
  for (const invoice of listResponse.data) {
    if (
      invoice.originatesFromContentType?.id ===
        ApartmentVacancyNotationConfig.contentType &&
      invoice.originatesFromObjectId
    ) {
      apartmentVacancyNotationIds.add(invoice.originatesFromObjectId);
    }
    if (
      invoice.originatesFromContentType?.id ===
        IndustrialPremisesVacancyNotationConfig.contentType &&
      invoice.originatesFromObjectId
    ) {
      industrialPremisesVacancyNotationIds.add(invoice.originatesFromObjectId);
    }
    if (
      invoice.originatesFromContentType?.id ===
        ParkingSpotVacancyNotationConfig.contentType &&
      invoice.originatesFromObjectId
    ) {
      parkingSpotVacancyNotationIds.add(invoice.originatesFromObjectId);
    }
    if (
      invoice.originatesFromContentType?.id ===
        OutdoorSectionVacancyNotationConfig.contentType &&
      invoice.originatesFromObjectId
    ) {
      outdoorSectionVacancyNotationIds.add(invoice.originatesFromObjectId);
    }
  }

  const chunkedApartmentVacancyNotationIds = chunkIds(
    apartmentVacancyNotationIds
  );
  const resolvedApartmentVacancyNotationPromises = await Promise.allSettled(
    chunkedApartmentVacancyNotationIds.map((chunk) =>
      getList<ApartmentVacancyNotation>({
        modelName: 'apartmentvacancynotation',
        queryParams: {
          page: 1,
          pageSize: chunk.length,
          filters: {
            id: {
              __in: chunk.join(','),
            },
          },
          order: ['-createdAt'],
        },
      })
    )
  );

  const resolvedApartmentVacancyNotations: ApartmentVacancyNotation[] = [];
  const failedApartmentVacancyNotations: unknown[] = [];
  for (const promise of resolvedApartmentVacancyNotationPromises) {
    if (promise.status === 'fulfilled') {
      resolvedApartmentVacancyNotations.push(...(promise.value.list ?? []));
    } else {
      failedApartmentVacancyNotations.push(promise.reason);
    }
  }

  const chunkedIndustrialPremisesVacancyNotationIds = chunkIds(
    industrialPremisesVacancyNotationIds
  );
  const resolvedIndustrialPremisesVacancyNotationPromises =
    await Promise.allSettled(
      chunkedIndustrialPremisesVacancyNotationIds.map((chunk) =>
        getList<IndustrialPremisesVacancyNotation>({
          modelName: 'industrialpremisesvacancynotation',
          queryParams: {
            page: 1,
            pageSize: chunk.length,
            filters: {
              id: {
                __in: chunk.join(','),
              },
            },
            order: ['-createdAt'],
          },
        })
      )
    );

  const resolvedIndustrialPremisesVacancyNotations: IndustrialPremisesVacancyNotation[] =
    [];
  const failedIndustrialPremisesVacancyNotations: unknown[] = [];
  for (const promise of resolvedIndustrialPremisesVacancyNotationPromises) {
    if (promise.status === 'fulfilled') {
      resolvedIndustrialPremisesVacancyNotations.push(
        ...(promise.value.list ?? [])
      );
    } else {
      failedIndustrialPremisesVacancyNotations.push(promise.reason);
    }
  }

  const chunkedParkingSpotVacancyNotationIds = chunkIds(
    parkingSpotVacancyNotationIds
  );
  const resolvedParkingSpotVacancyNotationPromises = await Promise.allSettled(
    chunkedParkingSpotVacancyNotationIds.map((chunk) =>
      getList<ParkingSpotVacancyNotation>({
        modelName: 'parkingspotvacancynotation',
        queryParams: {
          page: 1,
          pageSize: chunk.length,
          filters: {
            id: {
              __in: chunk.join(','),
            },
          },
          order: ['-createdAt'],
        },
      })
    )
  );

  const resolvedParkingSpotVacancyNotations: ParkingSpotVacancyNotation[] = [];
  const failedParkingSpotVacancyNotations: unknown[] = [];
  for (const promise of resolvedParkingSpotVacancyNotationPromises) {
    if (promise.status === 'fulfilled') {
      resolvedParkingSpotVacancyNotations.push(...(promise.value.list ?? []));
    } else {
      failedParkingSpotVacancyNotations.push(promise.reason);
    }
  }

  const chunkedOutdoorSectionVacancyNotationIds = chunkIds(
    outdoorSectionVacancyNotationIds
  );
  const resolvedOutdoorSectionVacancyNotationPromises =
    await Promise.allSettled(
      chunkedOutdoorSectionVacancyNotationIds.map((chunk) =>
        getList<OutdoorSectionVacancyNotation>({
          modelName: 'outdoorsectionvacancynotation',
          queryParams: {
            page: 1,
            pageSize: chunk.length,
            filters: {
              id: {
                __in: chunk.join(','),
              },
            },
            order: ['-createdAt'],
          },
        })
      )
    );

  const resolvedOutdoorSectionVacancyNotations: OutdoorSectionVacancyNotation[] =
    [];
  const failedOutdoorSectionVacancyNotations: unknown[] = [];
  for (const promise of resolvedOutdoorSectionVacancyNotationPromises) {
    if (promise.status === 'fulfilled') {
      resolvedOutdoorSectionVacancyNotations.push(
        ...(promise.value.list ?? [])
      );
    } else {
      failedOutdoorSectionVacancyNotations.push(promise.reason);
    }
  }
  if (
    resolvedApartmentVacancyNotations.length === 0 &&
    resolvedIndustrialPremisesVacancyNotations.length === 0 &&
    resolvedParkingSpotVacancyNotations.length === 0 &&
    resolvedOutdoorSectionVacancyNotations.length === 0
  ) {
    return listResponse;
  }

  // fetch spaces based on notations

  const apartmentIds: Set<string> = new Set();
  const industrialPremisesIds: Set<string> = new Set();
  const parkingSpotIds: Set<string> = new Set();
  const outdoorSectionIds: Set<string> = new Set();

  // Extract unique IDs
  for (const apartmentNotation of resolvedApartmentVacancyNotations) {
    if (apartmentNotation.space?.id) {
      apartmentIds.add(apartmentNotation.space?.id);
    }
  }

  for (const industrialPremisesNotation of resolvedIndustrialPremisesVacancyNotations) {
    if (industrialPremisesNotation.space?.id) {
      industrialPremisesIds.add(industrialPremisesNotation.space?.id);
    }
  }

  for (const parkingSpotNotation of resolvedParkingSpotVacancyNotations) {
    if (parkingSpotNotation.space?.id) {
      parkingSpotIds.add(parkingSpotNotation.space?.id);
    }
  }

  for (const outdoorSectionNotation of resolvedOutdoorSectionVacancyNotations) {
    if (outdoorSectionNotation.space?.id) {
      outdoorSectionIds.add(outdoorSectionNotation.space?.id);
    }
  }

  const chunkedApartmentIds = chunkIds(apartmentIds);
  const resolvedApartmentPromises = await Promise.allSettled(
    chunkedApartmentIds.map((chunk) =>
      getList<Apartment>({
        modelName: 'apartment',
        queryParams: {
          filters: {
            id: {
              __in: chunk.join(','),
            },
          },
        },
      })
    )
  );

  const resolvedApartments: Apartment[] = [];
  const failedApartments: unknown[] = [];
  for (const promise of resolvedApartmentPromises) {
    if (promise.status === 'fulfilled') {
      resolvedApartments.push(...(promise.value.list ?? []));
    } else {
      failedApartments.push(promise.reason);
    }
  }

  const chunkedIndustrialPremisesIds = chunkIds(industrialPremisesIds);
  const resolvedIndustrialPremisesPromises = await Promise.allSettled(
    chunkedIndustrialPremisesIds.map((chunk) =>
      getList<IndustrialPremises>({
        modelName: 'industrialpremises',
        queryParams: {
          filters: {
            id: {
              __in: chunk.join(','),
            },
          },
        },
      })
    )
  );

  const resolvedIndustrialPremises: IndustrialPremises[] = [];
  const failedIndustrialPremises: unknown[] = [];
  for (const promise of resolvedIndustrialPremisesPromises) {
    if (promise.status === 'fulfilled') {
      resolvedIndustrialPremises.push(...(promise.value.list ?? []));
    } else {
      failedIndustrialPremises.push(promise.reason);
    }
  }

  const chunkedParkingSpotIds = chunkIds(parkingSpotIds);
  const resolvedParkingSpotPromises = await Promise.allSettled(
    chunkedParkingSpotIds.map((chunk) =>
      getList<ParkingSpot>({
        modelName: 'parkingspot',
        queryParams: {
          filters: {
            id: {
              __in: chunk.join(','),
            },
          },
        },
      })
    )
  );

  const resolvedParkingSpots: ParkingSpot[] = [];
  const failedParkingSpots: unknown[] = [];
  for (const promise of resolvedParkingSpotPromises) {
    if (promise.status === 'fulfilled') {
      resolvedParkingSpots.push(...(promise.value.list ?? []));
    } else {
      failedParkingSpots.push(promise.reason);
    }
  }

  const chunkedOutdoorSectionIds = chunkIds(outdoorSectionIds);
  const resolvedOutdoorSectionPromises = await Promise.allSettled(
    chunkedOutdoorSectionIds.map((chunk) =>
      getList<OutdoorSection>({
        modelName: 'outdoorsection',
        queryParams: {
          filters: {
            id: {
              __in: chunk.join(','),
            },
          },
        },
      })
    )
  );

  const resolvedOutdoorSections: OutdoorSection[] = [];
  const failedOutdoorSections: unknown[] = [];
  for (const promise of resolvedOutdoorSectionPromises) {
    if (promise.status === 'fulfilled') {
      resolvedOutdoorSections.push(...(promise.value.list ?? []));
    } else {
      failedOutdoorSections.push(promise.reason);
    }
  }

  // Map fetched data back to the listResponse
  const mappedListResponse: TCustomList<VacancyInvoice> = {
    data: listResponse.data.map((invoice) => {
      if (
        invoice.originatesFromContentType?.id ===
          ApartmentVacancyNotationConfig.contentType &&
        invoice.originatesFromObjectId
      ) {
        const apartmentVacancyNotation = resolvedApartmentVacancyNotations.find(
          (a) => a.id === invoice.originatesFromObjectId
        );
        // @ts-expect-error manual mapping
        invoice.notation = apartmentVacancyNotation;

        if (apartmentVacancyNotation?.space?.id) {
          const apartment = resolvedApartments.find(
            (a) => a.id === apartmentVacancyNotation?.space?.id
          );
          // @ts-expect-error manual mapping
          invoice.space = apartment;
        }
      }

      if (
        invoice.originatesFromContentType?.id ===
          IndustrialPremisesVacancyNotationConfig.contentType &&
        invoice.originatesFromObjectId
      ) {
        const industrialPremisesVacancyNotation =
          resolvedIndustrialPremisesVacancyNotations.find(
            (a) => a.id === invoice.originatesFromObjectId
          );
        // @ts-expect-error manual mapping
        invoice.notation = industrialPremisesVacancyNotation;

        if (industrialPremisesVacancyNotation?.space?.id) {
          const industrialPremises = resolvedIndustrialPremises.find(
            (a) => a.id === industrialPremisesVacancyNotation?.space?.id
          );
          // @ts-expect-error manual mapping
          invoice.space = industrialPremises;
        }
      }

      if (
        invoice.originatesFromContentType?.id ===
          ParkingSpotVacancyNotationConfig.contentType &&
        invoice.originatesFromObjectId
      ) {
        const parkingSpotVacancyNotation =
          resolvedParkingSpotVacancyNotations.find(
            (a) => a.id === invoice.originatesFromObjectId
          );
        // @ts-expect-error manual mapping
        invoice.notation = parkingSpotVacancyNotation;

        if (parkingSpotVacancyNotation?.space?.id) {
          const parkingSpot = resolvedParkingSpots.find(
            (a) => a.id === parkingSpotVacancyNotation?.space?.id
          );
          // @ts-expect-error manual mapping
          invoice.space = parkingSpot;
        }
      }

      if (
        invoice.originatesFromContentType?.id ===
          OutdoorSectionVacancyNotationConfig.contentType &&
        invoice.originatesFromObjectId
      ) {
        const outdoorSectionVacancyNotation =
          resolvedOutdoorSectionVacancyNotations.find(
            (a) => a.id === invoice.originatesFromObjectId
          );
        // @ts-expect-error manual mapping
        invoice.notation = outdoorSectionVacancyNotation;

        if (outdoorSectionVacancyNotation?.space?.id) {
          const outdoorSection = resolvedOutdoorSections.find(
            (a) => a.id === outdoorSectionVacancyNotation?.space?.id
          );
          // @ts-expect-error manual mapping
          invoice.space = outdoorSection;
        }
      }

      return {
        ...invoice,
      };
    }) as VacancyInvoice[],
  };

  return mappedListResponse;
}
